import * as ko from 'knockout';

import i18n from '../i18n';
import { ListRequestParams } from '../api/request';
import { ListLoader, ListLoaderDelegate } from '../components/list_loader';
import * as mmApi from '../api/measurement_metas';
import * as mmSuggestionsApi from '../api/v2/measurement_meta_suggestions';
import * as cropsApi from '../api/crops';
import * as dimensionsApi from '../api/dimensions';
import * as traitCategoriesApi from '../api/trait_categories';
import * as mmTagsApi from '../api/measurement_meta_tags';
import { BaseLoadingScreen } from './base_loading_screen';
import { deflateList } from '../api/serialization';
import { updateLocationWithQueryString, asArray, downloadBlob } from '../utils';
import { ATTRIBUTE_TYPES_WITH_DDM } from '../models/measurement_meta';
import { FilterDelegate } from '../components/list_filters';
import { canEditMMLibrary } from '../permissions';
import { session } from '../session';
import { Action } from '../components/basic_widgets';
import { openMeasurementLibraryTrials } from '../components/measurement_library_trials';
import { openCopyTraitToTrials } from '../components/copy_trait_to_trials';
import { ListHeaderAction } from '../components/list_header';
import { MeasurementMetaData } from '../api/datasets';
import { RuleGroupController, SerializedRuleGroup } from '../components/condition_builder';
import '../components/condition_builder_filter';
import { getMMTagSearchConfig } from '../components/configs/search_configs';
import { FormSelectSearchConfiguration } from '../components/form_select_search';

// Interface for the specific "yes", "no", "both" filter options

export interface NamedFilterOption {
  id: string;
  name: string;
}

const template = require('raw-loader!../../templates/measurement_meta_library.html').default;

class MeasurementMetaLibrary
  extends BaseLoadingScreen
  implements ListLoaderDelegate<mmApi.MeasurementMetaLibraryData, mmApi.MeasurementMetaLibraryData>
{
  private listLoader: ListLoader<mmApi.MeasurementMetaLibraryData, mmApi.MeasurementMetaLibraryData>;
  exporting = ko.observable(false);
  management: boolean;
  showDriver = session.tenant().tpp_enabled;
  canEdit = canEditMMLibrary();

  private nameFilter = ko.observable('');
  private cropFilter = ko.observableArray<dimensionsApi.DimensionData>(null);
  private traitCategoryFilter = ko.observableArray<traitCategoriesApi.TraitCategoryData>(null);
  tagController = new RuleGroupController<mmTagsApi.MeasurementMetaTagData>();
  tagSearchConfig: FormSelectSearchConfiguration<mmTagsApi.MeasurementMetaTagData> =
    getMMTagSearchConfig(ko.observableArray<mmTagsApi.MeasurementMetaTagData>());
  private managementFilter = ko.observableArray<NamedFilterOption>(null);
  private managementTraitTypesChoices: NamedFilterOption[] = [
    { id: 'management_trait', name: i18n.t('Management')() } as NamedFilterOption,
    { id: 'standard_trait', name: i18n.t('Standard')() } as NamedFilterOption,
  ]

  newFilters: FilterDelegate[] = [
    { title: i18n.t('Crop')(), entities: this.cropFilter, list: cropsApi.list },
  ];
  searchPlaceholder = i18n.t('Search trait')();
  // TODO: add boolean filter: type derived or not

  listActions = ko.observableArray<ListHeaderAction>();

  constructor(params: {
    management: boolean;
    filters: {
      name_prefix: string;
      crop_ids: string;
      trait_category_ids: string;
      mm_tag_filter: string;
      management_trait_type: string;
    };
  }) {
    super();

    this.management = params.management;

    if (this.canEdit) {
      this.listActions.push({
        title: i18n.t('Add')(),
        icon: 'add_circle',
        onClick: () => {
          const url = `/library/traits/new/?management=${this.management}`;
          window.location.href = session.toTenantPath(url);
        },
        tooltipTitle: this.management ? i18n.t('Add management trait')() : i18n.t('Add trait')(),
      });

      mmSuggestionsApi.count().then((response) => {
        const count = response.count;
        if (count > 0) {
          this.listActions.push({
            title: i18n.t('Review')(),
            chipText: count.toString(),
            icon: 'rate_review',
            href: '/library/traits/review/',
            tooltipTitle: i18n.t('Review {{count}} traits', { count })(),
          });
        }
      });
    }

    if (!params.management) {
      this.listActions.push({
        title: i18n.t('Export')(),
        icon: 'file_download',
        onClick: this.exportLibrary,
        loading: this.exporting,
      });

      this.newFilters.push({
        title: i18n.t(['trait_category_title', 'Trait Category'])(),
        entities: this.traitCategoryFilter,
        list: traitCategoriesApi.list,
      });
      const tagTitle = i18n.t(['trait_tags_title', 'Trait Tags'])();
      this.newFilters.push({
        componentName: 'condition-builder-filter',
        componentParams: {
          group: this.tagController.group,
          searchConfig: this.tagSearchConfig,
          onChange: () => {},
          label: tagTitle,
        },
        title: tagTitle,
        serializeParameter: (name) => {
          const serialized = this.tagController.snapshot();
          return serialized ? { [name]: JSON.stringify(serialized) } : {};
        },
        value: this.tagController.snapshot,
        resetAction: () => this.tagController.reset(),
      });
      this.newFilters.push({
        title: i18n.t(['trait_type_filter_title', 'Trait Type'])(),
        entities: this.managementFilter,
        list: () => {
          return Promise.resolve(this.managementTraitTypesChoices)},
      });
    }

    this.nameFilter(params.filters.name_prefix || '');
    this.nameFilter = this.nameFilter.extend({ throttle: 300 });

    const cropIds = asArray(params.filters.crop_ids);
    const traitCategoryIds = asArray(params.filters.trait_category_ids);

    const managementTraitTypes = asArray(params.filters.management_trait_type);
    if (managementTraitTypes.length > 0) {
      this.managementFilter(this.managementTraitTypesChoices.filter(
        (choice) => managementTraitTypes.includes(choice.id)
      ));
    }

    let serializedTagFilter: SerializedRuleGroup | null = null;
    if (params.filters.mm_tag_filter) {
      try {
        serializedTagFilter = JSON.parse(params.filters.mm_tag_filter) as SerializedRuleGroup;
      } catch (e) {
        serializedTagFilter = null;
      }
    }

    const cropPromise = cropIds.length > 0 ? cropsApi.list({ ids: cropIds }) : undefined;
    const traitCategoryPromise =
      traitCategoryIds.length > 0 ? traitCategoriesApi.list({ ids: traitCategoryIds }) : undefined;
    const tagHydratePromise = serializedTagFilter
      ? this.tagController.hydrate(serializedTagFilter, (ids) =>
          mmTagsApi.list({ ids }).then(
            (tags) => new Map(tags.map((tag) => [tag.id!, tag]))
          )
        )
      : undefined;
    const promise = Promise.all([cropPromise, traitCategoryPromise, tagHydratePromise]).then(
      ([crop, traitCategory]) => {
        this.cropFilter((crop as cropsApi.CropData[]) || []);
        this.traitCategoryFilter((traitCategory as traitCategoriesApi.TraitCategoryData[]) || []);
      }
    );
    this.loadedAfter(promise);
  }

  onReady(loader: ListLoader<mmApi.MeasurementMetaLibraryData, mmApi.MeasurementMetaLibraryData>) {
    this.listLoader = loader;
    this.nameFilter.subscribe(() => this.listLoader.forceLoad());
  }

  private serializedTagFilter(): string | null {
    const serialized = this.tagController.snapshot();
    return serialized ? JSON.stringify(serialized) : null;
  }

  fetch(params: ListRequestParams) {

    // we set it to null by default so both measurement_metas will be retrieved
    let managementTraitType: boolean | null = null;

    if (this.managementFilter && this.managementFilter().length > 0) {
      const options = this.managementFilter();
      if (options.length == 1) {
        const selectedOptionId = options[0].id;
        if (selectedOptionId === 'management_trait') {
          managementTraitType = true;
        } else if (selectedOptionId === 'standard_trait') {
          managementTraitType = false;
        }
      }
    }
    const filter = {
      name_prefix: this.nameFilter(),
      crop_ids: deflateList(this.cropFilter),
      trait_category_ids: deflateList(this.traitCategoryFilter),
      mm_tag_filter: this.serializedTagFilter(),
      management_trait_type: deflateList(this.managementFilter),
    };
    updateLocationWithQueryString(filter);


    return mmApi.list({
      only_for: null,
      management: managementTraitType,
      ...filter,
      ...params,
    });
  }

  instantiate(data: mmApi.MeasurementMetaLibraryData) {
    return data;
  }

  remove(id: string) {
    return mmApi.remove(id);
  }

  canRemove(mm: mmApi.MeasurementMetaLibraryData) {
    return this.canEdit;
  }

  getEditPageUrl(id: string) {
    return session.toTenantPath(`/library/traits/${id}/`);
  }

  navigateToEditPage(id: string) {
    const url = this.getEditPageUrl(id);
    window.location.href = url;
  }

  typeName(mm: mmApi.MeasurementMetaLibraryData) {
    return typeName(mm);
  }

  /**
   * Generates a string representation for min/max values for numeric and date traits.
   * @param measurementMeta measurement meta data.
   * @returns A string in format '{min} - {max}'. Empty string if there are no min/max limits.
   */
  minMaxValuesRepresentation(measurementMeta: mmApi.MeasurementMetaLibraryData) {
    let min, max;
    if (measurementMeta.type == 'date') {
      if (measurementMeta.validation.date_min_value != null) {
        min = new Date(measurementMeta.validation.date_min_value).toLocaleDateString();
      }
      if (measurementMeta.validation.date_max_value != null) {
        max = new Date(measurementMeta.validation.date_max_value).toLocaleDateString();
      }
    } else if (measurementMeta.type == 'decimal') {
      if (measurementMeta.validation.number_min_value != null) {
        min = measurementMeta.validation.number_min_value.toString();
      }
      if (measurementMeta.validation.number_max_value != null) {
        max = measurementMeta.validation.number_max_value.toString();
      }
    }

    if (min && max) {
      return `[${min}, ${max}]`;
    } else if (min) {
      return `≥ ${min}`;
    } else if (max) {
      return `≤ ${max}`;
    }
    return '';
  }

  unitName(mm: mmApi.MeasurementMetaLibraryData) {
    return unitName(mm);
  }

  getActions(mm: mmApi.MeasurementMetaLibraryData): Action[] {
    const actions: Action[] = [
      {
        icon: 'assignment',
        title: i18n.t('Trials')(),
        cssClass: '',
        onClick: () => openMeasurementLibraryTrials(mm.id),
      },
    ];

    if (this.canEdit) {
      actions.push({
        icon: 'add_task',
        title: i18n.t('Copy to trials')(),
        cssClass: '',
        onClick: () => openCopyTraitToTrials(mm.id, mm.name_slug, mm.name_json),
      });
    }

    return actions;
  }

  exportLibrary = async () => {
    this.exporting(true);

    try {
      const data = await mmApi.exportLibrary({
        name_prefix: this.nameFilter(),
        crop_ids: deflateList(this.cropFilter),
        trait_category_ids: deflateList(this.traitCategoryFilter),
        mm_tag_filter: this.serializedTagFilter(),
        only_for: null,
        management: this.management,
      });
      this.exporting(false);
      downloadBlob(data, 'traits_library.xlsx');
    } finally {
      this.exporting(false);
    }
  };
}

export function typeName(mm: mmApi.SelectedTraitData): string {
  if (mm.normalize) {
    if (mm.type === 'date') {
      return i18n.t('Days')();
    } else if (mm.trait_unit_num && mm.trait_unit_den) {
      return mm.trait_unit_num.description;
    }
  }

  for (const attr of ATTRIBUTE_TYPES_WITH_DDM) {
    if (attr.value === mm.type) {
      if (attr.value === 'choice' && mm.mt_rating) {
        return `${attr.name()}, ${i18n.t('Rating')()}`;
      }
      return attr.name();
    }
  }

  return '';
}
export function unitName(mm: MeasurementMetaData): string {
  if (mm.normalize) {
    if (mm.type === 'date') {
      return i18n.t('Days')();
    } else if (mm.trait_unit_num && mm.trait_unit_den) {
      return mm.trait_unit_den.description;
    }
  }

  if (mm.type == 'derived' && mm.unit_formula) {
    return mm.unit_formula;
  }

  if (mm.unit) {
    return mm.unit.description;
  }

  return '';
}

export const measurementMetaLibrary = {
  name: 'measurement-meta-library',
  viewModel: MeasurementMetaLibrary,
  template: template,
};

ko.components.register(measurementMetaLibrary.name, measurementMetaLibrary);
